// SolicitanteUnidadesOrganizacionalesModal.jsx
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';

const EMPTY = {
  idSolicitante: 0,
  idUnidadOrganizacional: 0,
  activo: true, // se mantiene en el payload, pero no se muestra en UI
};

// 🔗 Endpoints / modelos para armar nombres de solicitante y UO
const endpointSolicitantes = ENDPOINTS.solicitantes;
const modeloSolicitante = Models.solicitantes;

const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointUnidadesOrg = ENDPOINTS.unidadesOrg;
const modeloUnidadesOrg = Models.unidadesOrg;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);

  // Opciones para los selects
  const [solicitanteOptions, setSolicitanteOptions] = useState([]);
  const [unidadOrgOptions, setUnidadOrgOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Cargar combos de Solicitante y Unidad Org. cuando se abre el modal
  useEffect(() => {
    if (!open) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [
          resSolicitantes,
          resEmpleados,
          resPersonas,
          resUnidadesOrg,
        ] = await Promise.all([
          axios.get(endpointSolicitantes),
          axios.get(endpointEmpleado),
          axios.get(endpointPersona),
          axios.get(endpointUnidadesOrg),
        ]);

        const solicitantesNorm = (resSolicitantes.data || []).map(
          (x) => modeloSolicitante(x)
        );
        const empleadosNorm = (resEmpleados.data || []).map((x) =>
          modeloEmpleado(x)
        );
        const personasNorm = (resPersonas.data || []).map((x) =>
          modeloPersona(x)
        );
        const unidadesNorm = (resUnidadesOrg.data || []).map((x) =>
          modeloUnidadesOrg(x)
        );

        // Armar opciones de solicitante (nombre completo)
        const solOpts = solicitantesNorm.map((sol) => {
          const emp = empleadosNorm.find(
            (e) => e.idEmpleado === sol.idEmpleado
          );
          const per = emp
            ? personasNorm.find(
                (p) => p.idPersona === emp.idPersona
              )
            : null;

          const fullName = per
            ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${
                per.apellidoMaterno || ''
              }`.trim()
            : '';

          const label =
            fullName && fullName.length > 0
              ? `${fullName}`
              : `Solicitante #${sol.idSolicitante}`;

          return {
            id: sol.idSolicitante,
            label,
          };
        });

        // Armar opciones de unidad organizacional
        const uoOpts = unidadesNorm.map((uo) => {
          const nombreUo =
            (
              uo.nombreUnidadOrganizacional ||
              uo.nombreUnidadOrg ||
              uo.nombre ||
              uo.descripcion ||
              ''
            ).toString() || '';

          const label =
            nombreUo && nombreUo.length > 0
              ? `${nombreUo} (${uo.clave})`
              : `Unidad Org. #${uo.idUnidadOrganizacional}`;

          return {
            id: uo.idUnidadOrganizacional,
            label,
          };
        });

        setSolicitanteOptions(solOpts);
        setUnidadOrgOptions(uoOpts);
      } catch (err) {
        console.error(err);
        alert(
          'Error al cargar la lista de solicitantes o unidades organizacionales.'
        );
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [open]);

  // Cargar datos de edición
  useEffect(() => {
    if (mode === 'edit' && data) {
      setForm({
        idSolicitanteUnidadesOrganizacionales:
          data.idSolicitanteUnidadesOrganizacionales ?? 0,
        idSolicitante: data.idSolicitante ?? 0,
        idUnidadOrganizacional: data.idUnidadOrganizacional ?? 0,
        activo: data.activo ?? true,
      });
    } else if (mode === 'new') {
      setForm(EMPTY);
    }
  }, [mode, data]);

  if (!open) return null;

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}
    >
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new'
          ? 'Nueva relación solicitante/unidad org.'
          : 'Editar relación solicitante/unidad org.'}
      </h3>
      <button
        className="roles-btn"
        onClick={onClose}
        aria-label="Cerrar"
      >
        ✕
      </button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}
    >
      {/* SELECT Solicitante */}
      <div>
        <label
          className="roles-title"
          style={{ fontSize: '.95rem' }}
        >
          Solicitante:
        </label>
        <br />
        <br />
        <select
          className="infoField"
          value={form.idSolicitante || ''}
          disabled={loadingOptions}
          onChange={(e) =>
            setForm({
              ...form,
              idSolicitante: Number(e.target.value) || 0,
            })
          }
        >
          <option value="">
            {loadingOptions
              ? 'Cargando solicitantes...'
              : 'Seleccione un solicitante'}
          </option>
          {solicitanteOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* SELECT Unidad Organizacional */}
      <div>
        <label
          className="roles-title"
          style={{ fontSize: '.95rem' }}
        >
          Unidad Organizacional:
        </label>
        <br />
        <br />
        <select
          className="infoField"
          value={form.idUnidadOrganizacional || ''}
          disabled={loadingOptions}
          onChange={(e) =>
            setForm({
              ...form,
              idUnidadOrganizacional:
                Number(e.target.value) || 0,
            })
          }
        >
          <option value="">
            {loadingOptions
              ? 'Cargando unidades org...'
              : 'Seleccione una unidad organizacional'}
          </option>
          {unidadOrgOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* activo se conserva en form pero no se muestra */}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>
        Cancelar
      </button>
      <button
        className="btn-action"
        onClick={() => onSubmit(form, mode)}
        disabled={
          !form.idSolicitante ||
          !form.idUnidadOrganizacional ||
          loadingOptions
        }
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div
        className="roles-inline-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
